<template>
  <div
    class="academy-event"
    :class="{ 'academy-event--mobile': isMobile }"
  >
    <header class="ae-head">
      <div class="ae-title">
        <div class="ae-tile">
          <v-icon
            size="22"
            color="primary"
          >
            mdi-school-outline
          </v-icon>
        </div>
        <div class="ae-title-text">
          <div class="ae-title-main">
            {{ $t('academyEvent') }}
            <span
              v-if="name"
              class="ae-title-name"
            >· {{ name }}</span>
          </div>
          <div class="ae-title-sub">
            {{ $t('academyEventFormHint') }}
          </div>
        </div>
      </div>
      <div class="ae-head-tools">
        <label
          class="ae-switch"
          :class="{ 'is-on': status }"
        >
          <v-switch
            v-model="status"
            value="active"
            inset
            dense
            hide-details
          />
          <span>{{ $t('status') }}: {{ (status ? $t('active') : $t('deactive')) }}</span>
        </label>
        <button
          type="button"
          class="ae-icon"
          :title="$t('academyEventList')"
          @click="goTo( 'academyEventList')"
        >
          <v-icon size="18">
            mdi-format-list-numbered
          </v-icon>
        </button>
        <button
          type="button"
          class="ae-icon"
          :title="$t('back')"
          @click="$router.go(-1)"
        >
          <v-icon size="18">
            mdi-arrow-left
          </v-icon>
        </button>
      </div>
    </header>
    <v-progress-linear
      :active="!render"
      :indeterminate="!render"
      color="primary"
      height="2"
    />
    <v-form
      v-if="render"
      ref="form"
      v-model="valid"
      class="ae-form"
      lazy-validation
    >
      <div class="ae-body">
        <aside class="ae-side">
          <section class="ae-card">
            <div class="ae-card-head">
              <div class="ae-card-tile">
                <v-icon size="20">
                  mdi-information-outline
                </v-icon>
              </div>
              <div class="ae-card-title">
                {{ $t('academyEventSectionInfo') }}
              </div>
            </div>
            <div class="ae-toggles">
              <label
                class="ae-toggle"
                :class="{ 'is-on': type }"
              >
                <v-switch
                  v-model="type"
                  value="interview"
                  inset
                  dense
                  hide-details
                />
                <span>{{ $t('inerview') }}</span>
              </label>
              <label
                v-show="$store.state.absentSms == 'active'"
                class="ae-toggle"
                :class="{ 'is-on': absentSms }"
              >
                <v-switch
                  v-model="absentSms"
                  value="active"
                  inset
                  dense
                  hide-details
                />
                <span>{{ $t('absentSms') }}</span>
              </label>
            </div>
            <div class="ae-grid">
              <div class="input-minimal ae-span">
                <v-text-field
                  v-model="name"
                  :label="$t('name')"
                  dense
                  :rules="[v => !!v || $t('is-required')]"
                  outlined
                  hide-details="auto"
                  clearable
                />
              </div>
              <div class="input-minimal">
                <autocomplete
                  v-model="category"
                  title="academyCategory"
                  type="academyCategory"
                  :value="category"
                />
              </div>
              <div class="input-minimal">
                <v-text-field
                  v-model="number"
                  :label="$t('number')"
                  dense
                  :rules="[v => !!v || $t('is-required')]"
                  outlined
                  hide-details="auto"
                  clearable
                  @blur="getEventNumber"
                />
              </div>
              <div
                v-if="!type"
                class="input-minimal"
              >
                <money
                  v-model="amount"
                  :label="$t('eventAmount')"
                  :dense="true"
                  :outlined="true"
                  :solo="false"
                  :clearable="true"
                  value-when-is-empty=""
                  :options="options"
                  class="dense"
                />
              </div>
              <div class="input-minimal">
                <autocomplete
                  v-model="branch"
                  :title="$t('branch')"
                  type="branch"
                  :set-init="true"
                  :value="branch"
                />
              </div>
              <div class="input-minimal ae-span">
                <tag
                  v-model="tag"
                  :solo="false"
                  :input="tag"
                />
              </div>
              <button
                type="button"
                class="ae-color ae-span"
                @click="colorPickerdialog = true"
              >
                <span
                  class="ae-color-swatch"
                  :class="{ 'primary': !eventColor || eventColor === 'primary' }"
                  :style="(eventColor && eventColor !== 'primary' ? { background: eventColor } : {})"
                />
                <span>{{ $t('selectColor') }}</span>
                <v-icon
                  size="18"
                  class="ae-color-go"
                >
                  mdi-palette-outline
                </v-icon>
              </button>
            </div>
          </section>

          <section class="ae-card">
            <div class="ae-card-head">
              <div class="ae-card-tile">
                <v-icon size="20">
                  mdi-account-tie-outline
                </v-icon>
              </div>
              <div class="ae-card-title">
                {{ $t('academyEventSectionTeacher') }}
              </div>
            </div>
            <div class="ae-grid">
              <div class="input-minimal ae-span">
                <autocomplete
                  v-model="place"
                  title="academyPlace"
                  type="academyPlace"
                  :value="place"
                />
              </div>
              <div class="input-minimal">
                <autocomplete
                  v-model="teacher"
                  title="teacher"
                  type="person"
                  :new-btn="true"
                  :data="JSON.stringify({type: 'teacher'})"
                  :value="teacher"
                />
              </div>
              <div class="input-minimal">
                <money
                  v-model="teacherCost"
                  :label="$t('teacherCost') + '(' + $t('perSession') + ')'"
                  :dense="true"
                  :outlined="true"
                  :solo="false"
                  :clearable="true"
                  value-when-is-empty=""
                  :options="options"
                  class="dense"
                />
              </div>
            </div>
          </section>

          <section class="ae-card">
            <div class="ae-card-head">
              <div class="ae-card-tile">
                <v-icon size="20">
                  mdi-calendar-clock-outline
                </v-icon>
              </div>
              <div class="ae-card-title">
                {{ $t('academyEventSectionSchedule') }}
              </div>
            </div>
            <div class="ae-grid">
              <div class="input-minimal">
                <v-text-field
                  id="sDate"
                  v-model="startDate"
                  :label="$t('startDate')"
                  :rules="[v => !!v || $t('is-required')]"
                  clear-icon="flaticon2-close-cross"
                  outlined
                  dense
                  hide-details="auto"
                  clearable
                />
                <date-picker
                  v-model="startDate"
                  element="sDate"
                  :locale="$store.state.date_locale"
                  :format="$store.state.date_format"
                  auto-submit
                  :min="$store.state.startDate"
                  :max="$store.state.endDate"
                  :color="this.$vuetify.theme.themes[this.$vuetify.theme.isDark ? 'dark' : 'light'].primary"
                  @close="setDate"
                />
              </div>
              <div class="input-minimal">
                <v-text-field
                  v-model="sessionCount"
                  :label="(type === 'interview' ? $t('sessionCount') : $t('sessionCount'))"
                  dense
                  outlined
                  hide-details="auto"
                  clearable
                  type="number"
                  :rules="numberRules"
                  @focus="focusEvent"
                />
              </div>
              <div class="input-minimal">
                <v-menu
                  ref="startTimeRef"
                  v-model="menu1"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  :return-value.sync="startTime"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="startTime"
                      :label="$t('startTime')"
                      append-icon="mdi-clock-time-four-outline"
                      readonly
                      dense
                      outlined
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-time-picker
                    v-if="menu1"
                    v-model="startTime"
                    class="mt-0"
                    :allowed-minutes="allowedStep"
                    format="24hr"
                    full-width
                    scrollable
                    @click:minute="$refs.startTimeRef.save(startTime)"
                  />
                </v-menu>
              </div>
              <div class="input-minimal">
                <v-menu
                  ref="duartionRef"
                  v-model="menu2"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  :return-value.sync="duration"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="duration"
                      :label="$t('duration')"
                      append-icon="mdi-timer-outline"
                      readonly
                      dense
                      outlined
                      hide-details
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-time-picker
                    v-if="menu2"
                    v-model="duration"
                    class="mt-0"
                    :allowed-minutes="allowedStep"
                    format="24hr"
                    full-width
                    scrollable
                    @click:minute="$refs.duartionRef.save(duration)"
                  />
                </v-menu>
              </div>
              <div
                v-if="type"
                class="input-minimal"
              >
                <v-text-field
                  v-model="sessionCountDay"
                  :label="$t('dayCountSession')"
                  dense
                  outlined
                  hide-details="auto"
                  clearable
                  type="number"
                  :rules="numberRules"
                  @focus="focusEvent"
                />
              </div>
              <div class="ae-span">
                <div class="ae-label">
                  {{ $t('daysHolding') }}
                </div>
                <v-chip-group
                  v-model="days"
                  class="ae-days"
                  column
                  multiple
                >
                  <v-chip
                    v-for="(day, i) in daysList"
                    :key="i"
                    small
                    :value="day"
                  >
                    {{ $t(day) }}
                  </v-chip>
                </v-chip-group>
              </div>
            </div>
          </section>
        </aside>
        <section class="ae-card ae-cal">
          <calender
            :events-prop="events"
            :place-prop="place"
            :start-time="startTime"
            :height-prop="layout.calendarHeight"
          />
        </section>
      </div>
      <footer class="ae-foot">
        <v-btn
          v-if="!sessionCreated"
          color="primary"
          outlined
          class="app-form-action-btn"
          :loading="isLoading"
          @click="review"
        >
          <v-icon
            left
            size="18"
          >
            mdi-calendar-search
          </v-icon>
          {{ $t('createAndReviewSessions') }}
        </v-btn>
        <v-btn
          v-if="!id"
          color="primary"
          depressed
          class="app-form-action-btn ms-2"
          :loading="isLoading"
          @click="create"
        >
          {{ $t('submit') }}
        </v-btn>
        <v-btn
          v-if="id"
          color="primary"
          depressed
          class="app-form-action-btn ms-2"
          :loading="isLoading"
          @click="update"
        >
          {{ $t('submit') }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="grey"
          outlined
          class="app-form-action-btn"
          @click="goTo( 'academyEventList' )"
        >
          {{ $t('academyEventList') }}
        </v-btn>
      </footer>
    </v-form>
    <v-dialog
      v-model="colorPickerdialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      width="340"
    >
      <v-card class="ae-color-dialog">
        <div class="ae-color-dialog-head">
          <div class="ae-card-tile">
            <v-icon size="20">
              mdi-palette-outline
            </v-icon>
          </div>
          <span>{{ $t('selectColor') }}</span>
          <span
            class="ae-color-swatch ae-color-current"
            :class="{ 'primary': !eventColor || eventColor === 'primary' }"
            :style="(eventColor && eventColor !== 'primary' ? { background: eventColor } : {})"
          />
        </div>
        <!-- own swatch grid: v-color-picker emitted a colour (yellow) on open because 'primary' is not a hex value -->
        <div class="ae-swatches">
          <button
            v-for="c in colorSwatches"
            :key="c"
            type="button"
            class="ae-swatch"
            :class="{ 'is-active': isColor(c) }"
            :style="{ background: c }"
            :title="c"
            @click="eventColor = c; colorPickerdialog = false"
          >
            <v-icon
              v-if="isColor(c)"
              size="16"
              color="white"
            >
              mdi-check
            </v-icon>
          </button>
        </div>
        <div class="ae-color-dialog-foot">
          <v-btn
            color="grey"
            outlined
            class="app-form-action-btn"
            @click="colorPickerdialog = !colorPickerdialog"
          >
            {{ $t('close') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <snakbar
      v-model="snakbar"
      :text="snakbarText"
      :active="snakbar"
      :color="snakbarColor"
    />
  </div>
</template>

<script>
  import Vue from 'vue'
  import General from '../../../../Mixins/general'
  import Calender from './partial/dashboardCalender'
  import Snakbar from '../../components/general/snakbar/snakbar'
  import aicomplete from '../../components/general/AIcomplete/index'
  export default {
    name: 'AcademyCategory',
    components: {
      aicomplete,
      Snakbar,
      Calender,
    },
    mixins: [General],
    props: ['formType'],
    data: () => ({
      branch: null,
      tag: null,
      render: false,
      isLoading: false,
      absentSms: null,
      number: 1,
      colorPickerdialog: false,
      sessionCreated: null,
      status: 'active',
      menu2: null,
      menu1: null,
      duration: '01:00',
      startTime: '12:00',
      eventColor: 'primary',
      amount: null,
      sessionCountDay: null,
      sessionCount: null,
      teacherCost: null,
      teacher: null,
      type: null,
      start: null,
      end: null,
      events: null,
      category: null,
      place: null,
      days: null,
      daysList: null,
      checkNumber: null,
      startDate: null,
      name: null,
      valid: null,
      numberRules: [
        v => !!v || 'This field is required',
        v => (v && v >= 0) || 'The number must be bigger than zero',
      ],
      timer: 0,
      message: null,
      id: null,
      loading: false,
    }),
    computed: {
      colorSwatches () {
        return [
          '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#1976D2',
          '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39',
          '#FFC107', '#FF9800', '#FF5722', '#795548', '#607D8B', '#424242',
          '#B71C1C', '#880E4F', '#4A148C', '#1A237E', '#004D40', '#1B5E20',
        ]
      },
    },
    created () {
      // UI-only state kept out of data(): create()/update() post this.$data
      this.layout = Vue.observable({ calendarHeight: 600 })
      this.onResize = () => {
        // 98 app chrome, header 60, footer 61, body padding 24, card border 2, calendar toolbar 44
        this.layout.calendarHeight = this.isMobile ? 480 : Math.max(320, window.innerHeight - (98 + 60 + 61 + 24 + 2 + 46))
      }
      this.onResize()
      window.addEventListener('resize', this.onResize)
    },
    beforeDestroy () {
      window.removeEventListener('resize', this.onResize)
    },
    watch: {
      snakbar () {
      },
      category (val, oldVal) {
        if (val) {
          if (!this.name) {
            this.name = val.name + '-' + this.number
          }
        }
        if (!this.tag && val.tags) {
          this.tag = val.tags
        }
      },
    },
    beforeMount () {
      if (this.$route.params.id != null) {
        this.show()
        this.id = this.$route.params.id
        this.render = false
      } else {
        this.render = true
        this.getEventNumber()
      }
      this.daysList = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    },
    mounted () {
    },
    methods: {
      isColor (c) {
        return typeof this.eventColor === 'string' && this.eventColor.slice(0, 7).toUpperCase() === c
      },
      allowedStep: m => m % 5 === 0,
      getDayNumber (day) {
        var number
        switch (day) {
          case 'Saturday':
            number = 1
            break
          case 'Sunday':
            number = 2
            break
          case 'Monday':
            number = 3
            break
          case 'Tuesday':
            number = 4
            break
          case 'Wednesday':
            number = 5
            break
          case 'Thursday':
            number = 6
            break
          case 'Friday':
            number = 7
            break
        }
        return number
      },
      review () {
        var app = this
        app.loading = true
        app.clearing = true
        app.showing = true
        app.$axios.get(this.$endpoint + 'academySession', {
          params: {
            checkEvent: 'active',
            type: app.type,
            name: app.name,
            days: app.days,
            startDate: app.startDate,
            startTime: app.startTime,
            duration: app.duration,
            sessionCount: app.sessionCount,
            sessionCountDay: app.sessionCountDay,
            teacher: app.teacher,
            teacherCost: app.teacherCost,
            color: (app.eventColor !== 'primary' ? app.eventColor : app.colors[app.rnd(0, app.colors.length - 1)]),
            place: app.place,
          },
        })
          .then(res => {
            res = res.data
            app.events = res
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      create () {
        var app = this
        app.isLoading = true
        app.$axios.post(this.$endpoint + 'academyEvents', app.$data)
          .then(res => {
            app.goTo('academyEventList')
          })
          .catch(err => {
            this.serverErrorHandeler(err)
          })
          .finally(() => (this.isLoading = false))
      },
      getEventNumber () {
        this.loading = true
        var app = this
        app.isLoading = true
        clearTimeout(this.timer)
        this.timer = setTimeout(function () {
          app.$axios.get(app.$endpoint + 'getdetail', {
            params: {
              id: app.id,
              number: app.number,
              select: 'academyGetNumberEvent',
            },
          })
            .then(res => {
              res = res.data
              if (res.status === 'error') {
                if (app.checkNumber) {
                  app.snakbarText = res.message
                  app.snakbarColor = 'pink'
                  app.snakbar = true
                }
                app.number = res.number
              }
              app.checkNumber = true
            })
            .catch(err => {
              this.serverErrorHandeler(err)
            })
            .finally(() => (app.isLoading = false))
        }, 50)
        this.loading = false
      },
      show () {
        var app = this
        app.loading = true
        app.clearing = true
        app.showing = true
        app.$axios.get(this.$endpoint + 'academyEvents/' + this.$route.params.id + '/edit', {
          params: {
            id: this.$route.params.id,
          },
        })
          .then(res => {
            res = res.data
            app.$nextTick(() => {
              app.id = res.id
              app.name = res.name
              app.type = res.type
              app.branch = res.branch
              app.amount = res.amount
              app.absentSms = res.absent_sms
              app.status = res.status
              app.number = res.number
              app.teacher = res.teacher
              app.teacherCost = res.teacher_cost
              app.place = res.place
              app.eventColor = res.color
              app.startDate = res.start_date
              app.startTime = res.start_time
              app.duration = res.duration
              app.duration = res.duration
              app.sessionCount = res.session_count
              app.category = res.category
              app.days = res.arrayDays
              app.tag = res.tags
              app.sessionCreated = res.sessionCreated
              app.render = true
            })
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.loading = false))
      },
      update () {
        var app = this
        app.isLoading = true
        app.$axios.put(this.$endpoint + 'academyEvents/' + this.$route.params.id, this.$data)
          .then(res => {
            res = res.data
            app.goTo('academyEventList')
          })
          .catch(err => {
            app.serverErrorHandeler(err)
          })
          .finally(() => (app.isLoading = false))
      },
    },
  }
</script>

<style scoped lang="scss">
.academy-event {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 98px);
  background: #f3f7fc;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 6px;
  overflow: hidden;
  ::v-deep .v-input { height: auto !important; }
}
.ae-head {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 60px;
  padding: 10px 16px;
  background: #fff;
  border-bottom: 1px solid rgba(15, 23, 42, .07);
}
.ae-title { display: flex; align-items: center; gap: 12px; min-width: 0; }
.ae-tile, .ae-card-tile {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: rgba(25, 118, 210, .1);
}
.ae-tile { width: 40px; height: 40px; }
.ae-title-main { font-weight: 600; font-size: .95rem; }
.ae-title-name { font-weight: 500; opacity: .7; }
.ae-title-sub { font-size: .78rem; opacity: .6; }
.ae-head-tools { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.ae-icon {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: rgba(15, 23, 42, .04);
  color: rgba(15, 23, 42, .7);
  .v-icon { color: inherit; }
  &:hover { background: rgba(25, 118, 210, .1); color: var(--v-primary-base, #1976d2); }
}
.ae-switch, .ae-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 34px;
  padding-inline: 4px 12px;
  border-radius: 6px;
  border: 1px solid rgba(15, 23, 42, .08);
  background: #fff;
  font-size: .8rem;
  cursor: pointer;
  white-space: nowrap;
  &.is-on { background: rgba(25, 118, 210, .06); border-color: rgba(25, 118, 210, .25); color: var(--v-primary-base, #1976d2); }
  ::v-deep .v-input--switch { margin: 0; padding: 0; }
  ::v-deep .v-input--selection-controls__input { margin-inline-end: 0; }
}
.ae-form {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.ae-body {
  flex: 1 1 auto;
  min-height: 0;
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 12px;
  padding: 12px;
}
.ae-side {
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-inline-end: 2px;
  > * { flex-shrink: 0; }
}
.ae-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 6px;
}
.ae-side .ae-card { padding: 12px 14px 14px; }
.ae-card-head { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.ae-card-tile {
  width: 32px;
  height: 32px;
  .v-icon { color: var(--v-primary-base, #1976d2); }
}
.ae-card-title { font-weight: 600; font-size: .85rem; }
.ae-toggles { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.ae-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 10px;
  ::v-deep .v-input { margin: 0; padding: 0; }
}
.ae-span { grid-column: 1 / -1; }
.ae-label { font-size: .78rem; opacity: .6; margin-bottom: 4px; }
.ae-days ::v-deep .v-chip {
  border-radius: 6px !important;
  background: rgba(25, 118, 210, .06) !important;
  border: 1px solid rgba(25, 118, 210, .12) !important;
  color: rgba(15, 23, 42, .75) !important;
  &.v-chip--active {
    background: #fff !important;
    border-color: var(--v-primary-base, #1976d2) !important;
    color: var(--v-primary-base, #1976d2) !important;
    font-weight: 600;
  }
  &::before { opacity: 0 !important; }
}
.ae-color {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 10px;
  border: 1px solid rgba(15, 23, 42, .15);
  border-radius: 6px;
  font-size: .82rem;
  text-align: start;
  span:nth-child(2) { flex: 1; }
  .ae-color-go { color: var(--v-primary-base, #1976d2); }
  &:hover { border-color: rgba(25, 118, 210, .45); }
}
.ae-color-swatch {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, .12);
}
.ae-cal { min-height: 0; overflow: hidden; }
.ae-foot {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid rgba(15, 23, 42, .07);
}
.ae-color-dialog { border-radius: 6px !important; overflow: hidden; }
.ae-color-dialog-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  font-weight: 600;
  font-size: .9rem;
  border-bottom: 1px solid rgba(15, 23, 42, .07);
  span:nth-child(2) { flex: 1; }
}
.ae-color-current { width: 26px; height: 26px; }
.ae-swatches {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  padding: 14px;
  background: #f3f7fc;
}
.ae-swatch {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: transform .12s;
  &:hover { transform: scale(1.08); }
  &.is-active { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--v-primary-base, #1976d2); }
}
.ae-color-dialog-foot {
  display: flex;
  justify-content: flex-end;
  padding: 10px 14px;
  border-top: 1px solid rgba(15, 23, 42, .07);
}
.theme--dark {
  .ae-swatches { background: #121212; }
  .ae-color-dialog-head, .ae-color-dialog-foot { border-color: rgba(255, 255, 255, .08); }
  .ae-swatch.is-active { box-shadow: 0 0 0 2px #1e1e1e, 0 0 0 4px #90caf9; }
}

.academy-event--mobile {
  height: auto;
  border: 0;
  border-radius: 0;
  overflow: visible;
  .ae-head { flex-wrap: wrap; padding: 12px; }
  .ae-head-tools { width: 100%; justify-content: flex-end; }
  .ae-body { grid-template-columns: 1fr; padding: 8px; }
  .ae-side { overflow: visible; }
  .ae-grid { grid-template-columns: 1fr; }
  .ae-foot { position: sticky; bottom: 0; z-index: 3; flex-wrap: wrap; gap: 8px; }
}

.theme--dark {
  .academy-event { background: #121212; border-color: rgba(255, 255, 255, .08); }
  .ae-head, .ae-card, .ae-foot { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
  .ae-tile, .ae-card-tile { background: rgba(100, 181, 246, .14); }
  .ae-card-tile .v-icon, .ae-color-go { color: #90caf9; }
  .ae-icon { background: rgba(255, 255, 255, .06); color: rgba(255, 255, 255, .8); }
  .ae-switch, .ae-toggle { background: #1e1e1e; border-color: rgba(255, 255, 255, .1); }
  .ae-switch.is-on, .ae-toggle.is-on { background: rgba(100, 181, 246, .12); color: #90caf9; }
  .ae-color { border-color: rgba(255, 255, 255, .15); }
  .ae-days ::v-deep .v-chip {
    background: rgba(100, 181, 246, .08) !important;
    border-color: rgba(100, 181, 246, .16) !important;
    color: rgba(255, 255, 255, .8) !important;
    &.v-chip--active { background: #2a2a2a !important; border-color: #90caf9 !important; color: #90caf9 !important; }
  }
}
</style>
